import React, { useCallback, useEffect, useState } from 'react';
import { Search, Scale, Send } from 'lucide-react';

import { API_BASE_URL } from '../../config';

const inputClass =
    'h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100';

const ManualBonusAdjustmentPanel = ({ onShowToast }) => {
    const [lookupMode, setLookupMode] = useState('user_id');
    const [lookupValue, setLookupValue] = useState('');
    const [user, setUser] = useState(null);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [recent, setRecent] = useState([]);
    const [loadingLookup, setLoadingLookup] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const loadRecent = useCallback(async () => {
        const response = await fetch(`${API_BASE_URL}/api/settings/loyalty/manual-adjustment/recent?limit=50`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить историю');
        setRecent(Array.isArray(body.items) ? body.items : []);
    }, []);

    useEffect(() => {
        loadRecent().catch((loadError) => {
            setError(loadError.message || 'Не удалось загрузить историю');
        });
    }, [loadRecent]);

    const handleLookup = async (event) => {
        event.preventDefault();
        const value = lookupValue.trim();
        if (!value) {
            setError('Введите идентификатор пользователя');
            return;
        }
        const params = new URLSearchParams();
        if (lookupMode === 'user_id') params.set('user_id', value);
        else if (lookupMode === 'email') params.set('email', value);
        else params.set('tg_id', value);

        try {
            setLoadingLookup(true);
            setError('');
            const response = await fetch(
                `${API_BASE_URL}/api/settings/loyalty/manual-adjustment/lookup?${params.toString()}`,
                { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
            );
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Пользователь не найден');
            setUser(body);
        } catch (lookupError) {
            setUser(null);
            setError(lookupError.message || 'Пользователь не найден');
        } finally {
            setLoadingLookup(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!user?.user_id) {
            setError('Сначала найдите пользователя');
            return;
        }
        const parsedAmount = Number.parseInt(String(amount), 10);
        if (!Number.isFinite(parsedAmount) || parsedAmount === 0) {
            setError('Укажите ненулевую сумму корректировки');
            return;
        }
        if (reason.trim().length < 8) {
            setError('Причина должна содержать не менее 8 символов');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/loyalty/manual-adjustment`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user.user_id,
                    amount: parsedAmount,
                    reason: reason.trim(),
                }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось выполнить корректировку');
            setUser((current) => (current ? { ...current, bonus_balance: body.balance } : current));
            setAmount('');
            setReason('');
            await loadRecent();
            if (onShowToast) {
                onShowToast(
                    `Корректировка ${parsedAmount > 0 ? '+' : ''}${parsedAmount} применена. Баланс: ${body.balance}`,
                );
            }
        } catch (submitError) {
            setError(submitError.message || 'Не удалось выполнить корректировку');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {error && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {error}
                </div>
            )}

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <Search size={20} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900">Поиск пользователя</h3>
                        <p className="mt-1 text-sm text-slate-500">Найдите аккаунт по ID, email или Telegram ID.</p>
                    </div>
                </div>

                <form onSubmit={handleLookup} className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)_auto]">
                    <select
                        className={inputClass}
                        value={lookupMode}
                        onChange={(event) => setLookupMode(event.target.value)}
                        aria-label="Тип идентификатора"
                    >
                        <option value="user_id">user_id</option>
                        <option value="email">email</option>
                        <option value="tg_id">tg_id</option>
                    </select>
                    <input
                        className={inputClass}
                        value={lookupValue}
                        onChange={(event) => setLookupValue(event.target.value)}
                        placeholder={lookupMode === 'email' ? 'user@example.com' : '12345'}
                    />
                    <button
                        type="submit"
                        disabled={loadingLookup}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                    >
                        <Search size={16} /> {loadingLookup ? 'Поиск...' : 'Найти'}
                    </button>
                </form>

                {user && (
                    <div className="mt-5 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900">{user.fio || 'Без имени'}</div>
                        <div className="mt-1 grid gap-1 sm:grid-cols-2">
                            <span>ID: {user.user_id}</span>
                            <span>Баланс: {Number(user.bonus_balance || 0).toLocaleString('ru-RU')}</span>
                            <span>Email: {user.email || '—'}</span>
                            <span>tg_id: {user.tg_id || '—'}</span>
                        </div>
                    </div>
                )}
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                        <Scale size={20} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900">Корректировка баланса</h3>
                        <p className="mt-1 text-sm text-slate-500">Отрицательная сумма списывает бонусы. Причина обязательна.</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium text-slate-700">Сумма (может быть отрицательной)</span>
                        <input
                            className={inputClass}
                            type="number"
                            step="1"
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            placeholder="например, -50 или 100"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium text-slate-700">Причина (мин. 8 символов)</span>
                        <textarea
                            className="min-h-[110px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            maxLength={500}
                            placeholder="Компенсация по тикету #1234"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={submitting || !user}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Send size={16} /> {submitting ? 'Сохранение...' : 'Применить корректировку'}
                    </button>
                </form>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <h3 className="font-semibold text-slate-900">Последние корректировки</h3>
                <p className="mt-1 text-sm text-slate-500">Операции с типом manual_adjustment.</p>
                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
                            <tr>
                                <th className="py-2 pr-3 font-semibold">ID</th>
                                <th className="py-2 pr-3 font-semibold">User</th>
                                <th className="py-2 pr-3 font-semibold">Сумма</th>
                                <th className="py-2 pr-3 font-semibold">Баланс</th>
                                <th className="py-2 pr-3 font-semibold">Причина</th>
                                <th className="py-2 font-semibold">Когда</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-6 text-slate-500">Пока нет ручных корректировок.</td>
                                </tr>
                            ) : (
                                recent.map((row) => (
                                    <tr key={row.id} className="border-b border-slate-100 align-top">
                                        <td className="py-3 pr-3 font-medium text-slate-800">{row.id}</td>
                                        <td className="py-3 pr-3">{row.user_id}</td>
                                        <td className={`py-3 pr-3 font-semibold ${row.amount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                            {row.amount > 0 ? `+${row.amount}` : row.amount}
                                        </td>
                                        <td className="py-3 pr-3">{row.balance_after}</td>
                                        <td className="py-3 pr-3 text-slate-600">{row.details?.reason || '—'}</td>
                                        <td className="py-3 text-slate-500">
                                            {row.created_at ? new Date(row.created_at).toLocaleString('ru-RU') : '—'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default ManualBonusAdjustmentPanel;
